/**
 * Lanificio — proefstukken kiezen onder de productpagina van een kleed
 * (sections/lan-meer-ontwerpen.liquid).
 *
 * - "+ Proefstuk toevoegen" kiest een ontwerp ("✓ Proefstuk gekozen"); nog een
 *   klik zet het weer uit. Kiezen gebeurt alleen op de pagina.
 * - Maximaal data-max (3) proefstukken per bestelling. Proefstukken die al in
 *   de winkelwagen zitten tellen mee; staat een ontwerp er al in, dan staat
 *   de knop op "✓ In je winkelwagen". Is het maximum bereikt, dan worden de
 *   andere knoppen grijs: "Maximaal 3 proefstukken".
 * - Prijs: het eerste proefstuk in de bestelling data-first, elk volgend
 *   data-follow (de automatische kortingen "Proefstukken staffel" in Shopify).
 *   Het bedrag in de balk is wat er door deze keuze bij komt.
 * - "In winkelwagen" voegt alles in één keer toe via /cart/add.js, met
 *   dezelfde properties als de proefstukknop in het koopblok (Ontwerp,
 *   _ontwerp_handle, _ontwerp_afbeelding), werkt de winkelwagen bij via
 *   CartLinesUpdateEvent en opent daarna de cart drawer.
 * - Zolang de balk in beeld is, wijkt de sticky koopbalk (klasse op <html>).
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const ROOT = window.Shopify?.routes?.root || '/';
const euro = (cents) => '€ ' + (cents / 100).toLocaleString('nl-NL', { maximumFractionDigits: 2 });

async function getCart() {
  const response = await fetch(`${ROOT}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error('cart');
  return response.json();
}

function openCartDrawer() {
  const drawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
  if (drawer && typeof drawer.open === 'function' && !drawer.isOpen) drawer.open();
  else if (!drawer) window.location.href = `${ROOT}cart`;
}

class ProefstukKeuze {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.variantId = Number(root.dataset.variantId);
    this.first = Number(root.dataset.first) || 4500;
    this.follow = Number(root.dataset.follow) || 3500;
    this.max = Number(root.dataset.max) || 3;
    this.cards = [...root.querySelectorAll('[data-lan-keuze-card]')];
    this.sum = root.querySelector('[data-lan-keuze-sum]');
    this.add = root.querySelector('[data-lan-keuze-add]');
    this.error = root.querySelector('[data-lan-keuze-error]');
    this.chosen = new Set();
    this.inCart = new Set();
    this.cartCount = 0;
    this.busy = false;

    this.cards.forEach((card) => {
      card.querySelector('[data-lan-keuze-pick]').addEventListener('click', () => this.toggle(card));
    });
    this.add.addEventListener('click', () => this.submit());
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      if (event.detail?.source === 'lan-proefstuk-keuze') return;
      Promise.resolve(event.promise).finally(() => this.refresh());
    });
    this.watchBar();
    this.render();
    this.refresh();
  }

  /** Proefstukken in de winkelwagen ophalen (aantal en ontwerpen). */
  async refresh() {
    try {
      const cart = await getCart();
      const items = cart.items.filter((item) => item.handle === 'proefstuk');
      this.cartCount = items.reduce((total, item) => total + item.quantity, 0);
      this.inCart = new Set(items.map((item) => item.properties?._ontwerp_handle).filter(Boolean));
      this.inCart.forEach((handle) => this.chosen.delete(handle));
      this.render();
    } catch (e) {
      /* zonder winkelwagen-info blijft de keuze gewoon werken */
    }
  }

  toggle(card) {
    const handle = card.dataset.handle;
    if (this.inCart.has(handle)) return;
    if (this.chosen.has(handle)) this.chosen.delete(handle);
    else if (this.cartCount + this.chosen.size < this.max) this.chosen.add(handle);
    this.error.hidden = true;
    this.render();
  }

  /** Wat de gekozen proefstukken samen kosten, bovenop wat al in de winkelwagen zit. */
  total() {
    let cents = 0;
    for (let i = 0; i < this.chosen.size; i++) cents += this.cartCount + i === 0 ? this.first : this.follow;
    return cents;
  }

  render() {
    const full = this.cartCount + this.chosen.size >= this.max;
    this.cards.forEach((card) => {
      const button = card.querySelector('[data-lan-keuze-pick]');
      const label = card.querySelector('[data-lan-keuze-label]');
      const handle = card.dataset.handle;
      const picked = this.chosen.has(handle);
      const inCart = this.inCart.has(handle);
      button.setAttribute('aria-pressed', String(picked || inCart));
      button.disabled = inCart || (full && !picked);
      label.textContent = inCart
        ? '✓ In je winkelwagen'
        : picked
          ? '✓ Proefstuk gekozen'
          : full
            ? `Maximaal ${this.max} proefstukken`
            : '+ Proefstuk toevoegen';
    });

    const count = this.chosen.size;
    if (!count) {
      this.sum.textContent = 'Nog geen proefstukken gekozen';
      this.add.textContent = 'In winkelwagen';
      this.add.disabled = true;
      return;
    }
    const names = this.cards.filter((card) => this.chosen.has(card.dataset.handle)).map((card) => card.dataset.name);
    const price = euro(this.total());
    this.sum.textContent = `${count} ${count === 1 ? 'proefstuk' : 'proefstukken'} · ${names.join(', ')} · ${price}`;
    this.add.textContent = `In winkelwagen · ${price}`;
    this.add.disabled = this.busy;
  }

  async submit() {
    if (!this.chosen.size || this.busy) return;
    this.busy = true;
    this.error.hidden = true;
    this.render();

    const items = this.cards
      .filter((card) => this.chosen.has(card.dataset.handle))
      .map((card) => {
        const properties = { Ontwerp: card.dataset.title, _ontwerp_handle: card.dataset.handle };
        if (card.dataset.image) properties._ontwerp_afbeelding = card.dataset.image;
        return { id: this.variantId, quantity: 1, properties };
      });
    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.root.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map(() => ({ merchandiseId: String(this.variantId), quantity: 1 })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(`${ROOT}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sections.join(',') }),
      });
      const data = await response.json();
      const cart = await getCart();
      if (!response.ok || data.status) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, source: 'lan-proefstuk-keuze' },
        });
        throw new Error(data.description || data.message || 'add');
      }
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'lan-proefstuk-keuze', itemCount: items.length, sections: data.sections, didError: false },
      });
      this.chosen.clear();
      this.busy = false;
      await this.refresh();
      openCartDrawer();
    } catch (e) {
      this.busy = false;
      this.error.textContent = 'Dat lukte niet. Probeer het nog eens.';
      this.error.hidden = false;
      this.render();
    }
  }

  /** De sticky koopbalk van de productpagina wijkt voor de proefstukbalk. */
  watchBar() {
    const bar = this.root.querySelector('[data-lan-keuze-bar]');
    if (!bar || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(
      ([entry]) => document.documentElement.classList.toggle('lan-keuze-in-beeld', entry.isIntersecting),
      { rootMargin: '0px 0px 120px 0px' }
    ).observe(bar);
  }
}

document.querySelectorAll('[data-lan-keuze]').forEach((root) => {
  if (!root.dataset.ready) {
    root.dataset.ready = 'true';
    new ProefstukKeuze(root);
  }
});
